/**
 * Drawer section 4 (#1911) — GATE TWO: prove the channel before using it.
 *
 * `mail.*` is a new domain with no sending history. A first blast to the whole
 * customer list from a cold domain is how a sending reputation is destroyed
 * before it exists, and a misconfigured bulk credential fails identically on
 * one address and on nine hundred — except that only one of those is
 * recoverable. So a test send has to succeed before the real one unlocks, and
 * the button stays disabled rather than merely warning.
 *
 * ── ONE REHEARSAL, ONE SENDER (#1935) ───────────────────────────────────────
 * Every bulk mail leaves as `BULK_EMAIL_BRAND` whatever brand the recipient
 * signed up under, so one test send exercises the SMTP account, the verified
 * domain and the credential that the blast will use. The gate used to be per
 * brand — two cold domains, two proofs — and the operator picked which one to
 * rehearse here; with one sender that choice had exactly one right answer and
 * is gone. `/send` still refuses with 409 `EMAIL_NOTICE_TEST_SEND_REQUIRED`
 * until the proof row exists. An unconfigured channel is a 503 checked BEFORE
 * that, so this section never sends an operator to rehearse a channel that has
 * no credential to rehearse with.
 *
 * A rehearsal that FAILS is a third thing again: 502
 * `EMAIL_NOTICE_TEST_SEND_FAILED`, and deliberately not the unconfigured 503.
 * The channel resolved and the provider then refused the message, so the two
 * causes it actually catches are a malformed recipient and a rejected
 * credential — for neither of which is "retryable, a deployment is pending"
 * true. It writes no proof row, so a failed rehearsal leaves the verdict below
 * exactly where it was rather than half-opening the gate. This component
 * renders the server's message verbatim and branches on no code, so the
 * distinction is for whoever reads the envelope, not for the UI.
 *
 * The lamp is computed from `EmailNoticeDetail.proven_brands` rather than from
 * `notice.test_sent_at` alone — the rows are the evidence and the timestamp is
 * a summary. Because the proof set rides on the detail, the verdict is as
 * correct on a cold load as it is right after a rehearsal.
 *
 * ── THE CONFIRM DIALOG IS THE LAST THING READ ───────────────────────────────
 * The card above can carry any amount of context; the operator may not be
 * looking at it by the time they press. The dialog is the surface they
 * unavoidably read, so the two facts that make this irreversible — HOW MANY
 * PEOPLE, and NO RECALL — are its first line, ahead of anything reassuring.
 * The idempotence sentence is true and useful, and it is last, because read
 * first it means "this is safe to press".
 *
 * It names the SUBJECT, not the task title: the operator is about to put that
 * exact string in a customer's inbox, and confirming against their own
 * internal label would be confirming against the one of the two they are not
 * sending.
 */
import { useState } from 'react';
import type { BrandId, EmailNotice, EmailNoticeRecipient } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, ConfirmDialog, FormField, Input } from '../../ui/index.ts';
import { SectionCaption } from './SectionCaption.tsx';
import { evaluateGateTwo, type SendGateTwoState } from './noticeStatus.ts';
import type { NoticeSendProgress } from './noticeBatchSend.ts';

interface Props {
  readonly notice: EmailNotice;
  readonly recipients: readonly EmailNoticeRecipient[];
  /**
   * `EmailNoticeDetail.proven_brands` — every brand with a confirmed rehearsal
   * for this notice. Server state, so it survives a reload and is emptied by
   * the same accepted edit that deletes the proof rows.
   */
  readonly provenBrands: readonly BrandId[];
  readonly testSending: boolean;
  readonly sending: boolean;
  /** Non-null while a batched send run is walking the list (#2616). */
  readonly progress: NoticeSendProgress | null;
  readonly onTestSend: (to: string) => void;
  readonly onSend: () => void;
}

export function NoticeSendSection({
  notice,
  recipients,
  provenBrands,
  testSending,
  sending,
  progress,
  onTestSend,
  onSend,
}: Props): JSX.Element {
  const t = useT();
  const [testTo, setTestTo] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const recipientsFrozen = notice.recipients_finalized_at !== null;
  const terminal = notice.status === 'cancelled';
  // Pending = every row that has not been delivered, which is what a press
  // would actually mail. `recipients.length` would over-count on a re-press.
  const pending = recipients.filter((row) => row.status !== 'sent');
  const gateTwo = evaluateGateTwo({ testSentAt: notice.test_sent_at, provenBrands });
  const canSend =
    recipientsFrozen && gateTwo.kind === 'passed' && !terminal && pending.length > 0;

  return (
    <section className="flex flex-col gap-3" data-testid="email-notice-section-send">
      <SectionCaption
        index={4}
        title={t('email_notices.section.send')}
        hint={t('email_notices.section.send_hint')}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Chip tone={recipientsFrozen ? 'ok' : 'neutral'} testId="email-notice-gate-one">
          {recipientsFrozen
            ? t('email_notices.gate.one_passed')
            : t('email_notices.gate.one_blocked')}
        </Chip>
        <Chip tone={gateTwoTone(gateTwo)} testId="email-notice-gate-two">
          {gateTwoLabel(t, gateTwo)}
        </Chip>
      </div>

      <div className="flex flex-col gap-2 rounded border border-line-soft bg-well px-3 py-2.5">
        <FormField
          label={t('email_notices.send.test_to')}
          fieldId="notice-test-to"
          hint={t('email_notices.send.test_hint')}
        >
          <Input
            id="notice-test-to"
            type="email"
            mono
            value={testTo}
            disabled={terminal}
            onChange={(event) => setTestTo(event.target.value)}
            data-testid="email-notice-test-to"
          />
        </FormField>
        <div>
          <Button
            size="sm"
            variant="secondary"
            loading={testSending}
            disabled={terminal || !testTo.includes('@')}
            onClick={() => onTestSend(testTo)}
            data-testid="email-notice-test-send"
          >
            {t('email_notices.send.test_action')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Button
          variant="danger"
          loading={sending}
          disabled={!canSend}
          onClick={() => setConfirmOpen(true)}
          data-testid="email-notice-send"
        >
          {t('email_notices.send.action', { count: String(pending.length) })}
        </Button>
        {progress === null ? null : <SendProgressBar progress={progress} />}
        {canSend || progress !== null ? null : (
          <p className="m-0 text-micro text-paper-low" data-testid="email-notice-send-blocked">
            {blockedReason(t, {
              recipientsFrozen,
              gateTwo,
              terminal,
              frozen: recipients.length,
              pending: pending.length,
            })}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        tone="danger"
        title={t('email_notices.send.confirm_title')}
        body={
          <div className="flex flex-col gap-3">
            {/*
              Consequence FIRST, and it names the number. An operator who reads
              only the first line has still read the two things that cannot be
              undone.
            */}
            <p
              className="m-0 rounded border border-bad-line bg-bad-dim/50 px-2.5 py-2 text-dense text-bad-bright"
              data-testid="email-notice-send-confirm-consequence"
            >
              {t('email_notices.send.confirm_consequence', {
                count: String(pending.length),
                subject: notice.subject,
              })}
            </p>
            <p className="m-0">{t('email_notices.send.confirm_idempotence')}</p>
          </div>
        }
        // Typing the recipient count is the point: the number cannot be
        // scrolled past if it has to be reproduced.
        typeToConfirm={String(pending.length)}
        typeToConfirmLabel={t('email_notices.send.confirm_type_label')}
        confirmLabel={t('email_notices.send.confirm_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={sending}
        onConfirm={() => {
          onSend();
          setConfirmOpen(false);
        }}
        testId="email-notice-send-confirm"
      />
    </section>
  );
}

function SendProgressBar({ progress }: { readonly progress: NoticeSendProgress }): JSX.Element {
  const t = useT();
  const pct = progress.total === 0 ? 100 : (progress.done / progress.total) * 100;
  return (
    <div className="flex flex-col gap-1" data-testid="email-notice-send-progress">
      <div
        className="h-1.5 rounded-full bg-raised"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.done}
      >
        <div className="h-full rounded-full bg-signal" style={{ width: `${pct}%` }} />
      </div>
      <p className="m-0 text-micro text-paper-low">
        {t('email_notices.send.progress', {
          done: String(progress.done),
          total: String(progress.total),
          sent: String(progress.sent),
          failed: String(progress.failed),
        })}
      </p>
    </div>
  );
}

function gateTwoTone(state: SendGateTwoState): 'ok' | 'neutral' {
  // `ok` for `passed` ONLY — an unrehearsed gate is one the server will refuse,
  // not a near-miss to be rounded up.
  return state.kind === 'passed' ? 'ok' : 'neutral';
}

function gateTwoLabel(t: ReturnType<typeof useT>, state: SendGateTwoState): string {
  return state.kind === 'passed'
    ? t('email_notices.gate.two_passed')
    : t('email_notices.gate.two_blocked');
}

interface BlockedState {
  readonly recipientsFrozen: boolean;
  readonly gateTwo: SendGateTwoState;
  readonly terminal: boolean;
  /** Rows on the frozen list, delivered or not. */
  readonly frozen: number;
  readonly pending: number;
}

/**
 * One reason at a time, in the order they must be cleared. Listing all of them
 * would tell an operator with an empty recipient list to go do a test send.
 */
function blockedReason(t: ReturnType<typeof useT>, state: BlockedState): string {
  if (state.terminal) return t('email_notices.send.blocked_terminal');
  if (!state.recipientsFrozen) return t('email_notices.send.blocked_recipients');
  // A frozen list with no rows is NOT "everything has been delivered" — nothing
  // ever went out. Both readings leave the send blocked, but only one of them is
  // true, and the operator's next step differs: re-freeze against a wider
  // window, rather than conclude the notice is done.
  if (state.frozen === 0) return t('email_notices.send.blocked_empty_list');
  if (state.pending === 0) return t('email_notices.send.blocked_all_sent');
  if (state.gateTwo.kind === 'none') return t('email_notices.send.blocked_test');
  return '';
}
